import * as XLSX from 'xlsx-js-style';
import { buildReportWorkbook, applyWorkbookLtr } from '../../../Utils/ExcelReport';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import EnumsService from '../../../Services/enums.service';

/** Clients enabled for MASAV (draft rows) vs. active clients without MASAV. */
export const MASAV_EXPORT_KIND = {
    MASAV: 'masav',
    NON_MASAV: 'nonMasav',
};

const REPORT_COLORS = {
    primary: '#2F4BFF',
    primaryDark: '#1E308F',
    headerFont: '#FFFFFF',
    stripe: '#F6F8FF',
    totalFill: '#D7DFFF',
    text: '#1F2937',
    border: '#C3CBDB',
};

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const safeFileName = (name) => String(name || 'Report').replace(/[\\/:*?"<>|]/g, '_');

const formatMonth = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;
};

const formatNow = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(now.getHours())}:${pad(now.getMinutes())} ${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
};

/**
 * Column model shared by Excel and PDF. Rows are draft rows (see MasavDraftModal pickRow) extended with
 * `payable` (effective amount incl. manual override) and `status` (validation text, MASAV only).
 */
const buildColumns = (kind, t) => {
    const columns = [
        { label: '#', value: (_, i) => i + 1, width: 6 },
        { label: t('ClientName'), value: (r) => r.clientName, width: 32 },
        { label: t('Case'), value: (r) => r.caseId, width: 14 },
        { label: t('ClientId'), value: (r) => r.beneficiaryIdDigits || '', width: 14 },
        { label: t('MasavNetBalance'), value: (r) => r.netBalance, numeric: true },
        { label: t('MasavReconCurrentMonth'), value: (r) => r.currentMonthMonthlyCharge, numeric: true },
        { label: t('MasavBeyondMonth'), value: (r) => r.beyondCurrentMonth, numeric: true },
        { label: t('MasavPayableAmount'), value: (r) => r.payable, numeric: true },
    ];
    if (kind === MASAV_EXPORT_KIND.MASAV) {
        columns.push(
            { label: t('Bank'), value: (r) => r.bankCode || '', width: 8 },
            { label: t('Branch'), value: (r) => r.branchNumber || '', width: 8 },
            { label: t('AccountNumber'), value: (r) => r.accountNumber || '', width: 14 },
            { label: t('MasavPaymentDay'), value: (r) => r.paymentDayOfMonth ?? '', width: 10 },
            { label: t('MasavValidation'), value: (r) => r.status || '', width: 40, wide: true }
        );
    }
    return columns;
};

const getReportTitle = (kind, t) =>
    kind === MASAV_EXPORT_KIND.MASAV ? t('MasavExportTitleMasav') : t('MasavExportTitleNonMasav');

const getPeriodLine = ({ startDate, endDate, monthOnly, t }) => {
    const range = startDate
        ? `${t('StartDate')}: ${formatMonth(startDate)}   ${t('EndDate')}: ${formatMonth(endDate)}`
        : `${t('EndDate')}: ${formatMonth(endDate)}`;
    return monthOnly ? `${range}   (${t('MasavMonthOnly')})` : range;
};

const getClientLine = (client) =>
    [client?.name || client?.clientName || '', client?.caseId || ''].filter(Boolean).join(' · ');

const sumColumn = (rows, col) => round2(rows.reduce((acc, r, i) => acc + (Number(col.value(r, i)) || 0), 0));

const buildFileBaseName = (kind, endDate, t) =>
    safeFileName(`${getReportTitle(kind, t)}_${formatMonth(endDate).replace('/', '-')}`);

export const downloadMasavDraftExcel = ({ kind, rows, client, startDate, endDate, monthOnly, direction = 'rtl', t }) => {
    const columns = buildColumns(kind, t);
    const numericColIndexes = columns.map((c, i) => (c.numeric ? i : -1)).filter((i) => i >= 0);
    const dataRows = rows.map((r, i) =>
        columns.map((c) => {
            const v = c.value(r, i);
            return c.numeric ? round2(v) : v ?? '';
        })
    );
    const totalRow = columns.map((c, i) => {
        if (i === 1) return t('Total');
        return c.numeric ? sumColumn(rows, c) : '';
    });

    const wb = buildReportWorkbook({
        sheetName: 'Collection',
        titleLines: [getClientLine(client), getReportTitle(kind, t), getPeriodLine({ startDate, endDate, monthOnly, t })],
        header: columns.map((c) => c.label),
        rows: [...dataRows, totalRow],
        colWidths: columns.map((c) => c.width ?? 16),
        numericColIndexes,
        totalRowIndexes: [dataRows.length],
    });
    if (direction !== 'rtl') applyWorkbookLtr(wb);
    XLSX.writeFile(wb, `${buildFileBaseName(kind, endDate, t)}.xlsx`);
};

export const buildMasavDraftPdfHtml = ({ kind, rows, client, startDate, endDate, monthOnly, direction = 'rtl', t }) => {
    const columns = buildColumns(kind, t);
    const C = REPORT_COLORS;
    const title = getReportTitle(kind, t);
    const lang = direction === 'rtl' ? 'he' : 'en';
    const cellClass = (c) => (c.numeric ? 'num' : c.wide ? 'wide' : 'txt');

    const bodyRows = rows
        .map((r, i) => {
            const cells = columns
                .map((c) => {
                    const v = c.value(r, i);
                    const text = c.numeric ? beautifyNumber(round2(v)) : v;
                    return `<td class="${cellClass(c)}">${escapeHtml(text)}</td>`;
                })
                .join('');
            return `<tr class="${i % 2 === 1 ? 'stripe' : 'plain'}">${cells}</tr>`;
        })
        .join('');

    const totalCells = columns
        .map((c, i) => {
            const text = i === 1 ? t('Total') : c.numeric ? beautifyNumber(sumColumn(rows, c)) : '';
            return `<td class="${cellClass(c)}">${escapeHtml(text)}</td>`;
        })
        .join('');

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${direction}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 landscape; margin: 8mm 6mm; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, "Segoe UI", sans-serif;
      direction: ${direction};
      color: ${C.text};
      margin: 0;
      padding: 6px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .top-bar { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; font-size: 11px; font-weight: 700; }
    .center-block { text-align: center; flex: 1; }
    h1 { margin: 2px 0 4px; font-size: 18px; color: ${C.primary}; font-weight: 700; text-decoration: underline; }
    .meta { font-size: 11px; margin-bottom: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 9px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid ${C.border}; padding: 4px 3px; vertical-align: middle; }
    th { background: ${C.primary} !important; color: ${C.headerFont} !important; font-weight: 700; text-align: center; line-height: 1.2; }
    td.txt { text-align: center; }
    td.wide { text-align: start; font-size: 8px; }
    td.num { text-align: center; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
    tr.plain td { background: #FFFFFF !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td { font-weight: 700; background: ${C.totalFill} !important; color: ${C.primaryDark} !important; }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${escapeHtml(formatNow())}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(getClientLine(client))}</div>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">${escapeHtml(getPeriodLine({ startDate, endDate, monthOnly, t }))}</div>
    </div>
    <div></div>
  </div>
  <table>
    <thead><tr>${columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr></thead>
    <tbody>
      ${bodyRows}
      <tr class="total">${totalCells}</tr>
    </tbody>
  </table>
</body>
</html>`;
};

export const downloadMasavDraftPdf = async ({ token, ...options }) => {
    const caseId = options.client?.caseId;
    if (!token) throw new Error('Missing auth token');
    if (!caseId) throw new Error('Missing case id');

    const html = buildMasavDraftPdfHtml(options);
    const res = await EnumsService.GeneratePdfFromHtml(html, caseId, token);

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const data = await res.json();
        const pdfBase64 = data.data?.PdfBase64 || data.data?.pdfBase64 || data.PdfBase64 || data.pdfBase64;
        if (!pdfBase64) throw new Error('No PDF in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${buildFileBaseName(options.kind, options.endDate, options.t)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
